import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MenuService } from '../../../api/menuService';
import { MenuItemResponse } from '../../../@type/menu.types';

const MenuReorder: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menus, setMenus] = useState<MenuItemResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [draggedItem, setDraggedItem] = useState<number | null>(null);

  useEffect(() => {
    loadMenus();
  }, []);

  const loadMenus = async () => {
    setLoading(true);
    try {
      const response = await MenuService.getAllMenuItems();
      const sortedMenus = response.data.data.sort((a: MenuItemResponse, b: MenuItemResponse) => a.sort_order - b.sort_order);
      setMenus(sortedMenus);
    } catch (error) {
      console.error('Failed to load menus:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedItem(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    const newMenus = [...menus];
    const draggedMenu = newMenus[draggedItem];
    newMenus.splice(draggedItem, 1);
    newMenus.splice(index, 0, draggedMenu);

    setMenus(newMenus);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Update sort_order based on new positions
      const updates = menus.map((menu, index) => ({
        id: menu.id,
        sort_order: index + 1,
      }));

      // Save each menu with new sort_order
      for (const update of updates) {
        await MenuService.updateMenuItem(update.id, { sort_order: update.sort_order });
      }

      navigate('/settings/menus');
    } catch (error) {
      console.error('Failed to save menu order:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && menus.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <Typography variant="h4" className="font-bold">
          {t('menus.dragToReorder')}
        </Typography>
        <Typography variant="body2" className="mt-2" sx={{ color: 'var(--app-text-muted)' }}>
          Drag menu items to reorder them. The order will affect how they appear in the mobile app.
        </Typography>
      </div>

      <Paper className="p-6 max-w-3xl">
        <List className="border rounded">
          {menus.map((menu, index) => (
            <ListItem
              key={menu.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              divider
              className="cursor-move"
              sx={{
                '&:hover': { bgcolor: 'action.hover' },
                ...(draggedItem === index && { bgcolor: 'action.selected' }),
              }}
            >
              <IconButton edge="start" className="cursor-move mr-2">
                <DragIcon />
              </IconButton>
              <ListItemText
                primary={menu.code}
                secondary={`Route: ${menu.route_name}`}
                className="capitalize"
              />
              <Typography
                variant="body2"
                className="ml-4"
                sx={{ color: 'var(--app-text-muted)' }}
              >
                Order: {index + 1}
              </Typography>
            </ListItem>
          ))}
        </List>

        <div className="flex gap-4 justify-end mt-6">
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => navigate('/settings/menus')}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
            className="bg-buttton"
            sx={{ textTransform: 'capitalize' }}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Order'}
          </Button>
        </div>
      </Paper>
    </div>
  );
};

export default MenuReorder;
